import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronRight,
  Menu,
  X,
  Search,
  Bell,
  FileText,
  PlusCircle,
  User,
  LogOut,
  MessageSquare,
} from 'lucide-react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { handleLogout } from '../../utils/authUtils';

const MD_BREAKPOINT = 768;

const EditorLayout = () => {
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const [isMdUp, setIsMdUp] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= MD_BREAKPOINT : true
  );

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${MD_BREAKPOINT}px)`);
    const onChange = () => setIsMdUp(mq.matches);
    mq.addEventListener('change', onChange);
    setIsMdUp(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (typeof document === 'undefined' || document.head.querySelector('style[data-editor-header-responsive]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-editor-header-responsive', 'true');
    style.textContent = `
      @media (max-width: 640px) {
        .editor-panel-content [class~="justify-between"][class~="items-center"][class~="mb-6"] {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.75rem;
        }
        .editor-panel-content [class~="justify-between"][class~="items-center"][class~="mb-6"] > :last-child {
          align-self: flex-start;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const openMobileMenu = useCallback(() => setMobileMenuOpen(true), []);
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  const toggleDesktopCollapse = useCallback(() => setSidebarCollapsed((c) => !c), []);

  const sidebarWidthClass = isSidebarCollapsed ? 'md:w-20' : 'md:w-64';
  const mainMarginClass = isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64';

  const sidebarTransform = isMdUp
    ? 'translate-x-0'
    : mobileMenuOpen
    ? 'translate-x-0'
    : '-translate-x-full';

  return (
    <div className="flex min-h-screen min-h-[100dvh] bg-gray-50 overflow-hidden">
      {!isMdUp && mobileMenuOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={closeMobileMenu}
        />
      )}

      <aside
        className={`bg-gray-900 text-white flex flex-col fixed left-0 top-0 h-full max-h-screen z-50 w-64 max-w-[min(20rem,92vw)] md:max-w-none transition-[transform,width] duration-300 ease-out ${sidebarWidthClass} ${sidebarTransform} md:translate-x-0 shadow-xl md:shadow-none`}
      >
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-800 flex-shrink-0 gap-2">
          {!isSidebarCollapsed && (
            <img
              src="https://nchs-fe.vercel.app/images/alenalki.Logo.png"
              alt="Alenalki"
              className="h-8 sm:h-10 max-w-[140px] object-contain"
            />
          )}
          {isSidebarCollapsed && isMdUp && (
            <img
              src="https://nchs-fe.vercel.app/images/alenalki.Logo.png"
              alt=""
              className="h-8 mx-auto"
            />
          )}
          <button
            type="button"
            onClick={() => (isMdUp ? toggleDesktopCollapse() : closeMobileMenu())}
            className="text-gray-400 hover:text-white p-1 shrink-0 rounded-md hover:bg-gray-800"
            aria-label={isMdUp ? 'Toggle sidebar' : 'Close menu'}
          >
            {isMdUp ? (isSidebarCollapsed ? <ChevronRight size={18} /> : <X size={18} />) : (
              <X size={20} />
            )}
          </button>
        </div>

        <div className="px-4 py-2 sm:py-3 text-xs uppercase text-gray-400 font-semibold flex-shrink-0">
          {(!isSidebarCollapsed || !isMdUp) && 'Editor Panel'}
        </div>

        <SidebarNavigation
          collapsed={isSidebarCollapsed && isMdUp}
          currentPath={location.pathname}
          onNavigate={closeMobileMenu}
        />

        <div className="border-t border-gray-800 p-3 sm:p-4 mt-auto flex-shrink-0">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-medium">
                E
              </div>
            </div>
            {(!isSidebarCollapsed || !isMdUp) && (
              <div className="ml-3 min-w-0">
                <p className="text-sm font-medium truncate">Editor</p>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className={`mt-3 flex items-center text-red-400 hover:text-red-300 transition-colors w-full ${isSidebarCollapsed && isMdUp ? 'justify-center' : ''}`}
          >
            <LogOut size={18} />
            {(!isSidebarCollapsed || !isMdUp) && <span className="ml-2 text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      <div
        className={`flex-1 flex flex-col overflow-hidden min-w-0 w-full ${mainMarginClass} transition-[margin] duration-300 ease-out`}
      >
        <header className="bg-white border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 md:px-6 py-3 sm:py-4 shrink-0">
          <div className="flex items-center min-w-0 gap-2">
            <button
              type="button"
              className="md:hidden p-2 -ml-1 rounded-md text-gray-700 hover:bg-gray-100 shrink-0"
              onClick={openMobileMenu}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
            <div className="flex items-center text-xs sm:text-sm min-w-0">
              <span className="text-gray-500 shrink-0">Editor</span>
              <ChevronRight size={14} className="mx-1 text-gray-400 shrink-0" />
              <span className="font-medium truncate">{getCurrentPageTitle(location.pathname)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto justify-end">
            <div className="relative flex-1 sm:flex-initial max-w-full sm:max-w-xs min-w-0">
              <input
                type="text"
                placeholder="Search..."
                className="w-full py-2 pl-9 pr-3 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <Search size={16} className="absolute left-2.5 top-2.5 text-gray-400 pointer-events-none" />
            </div>
            <button
              type="button"
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100 relative shrink-0"
              aria-label="Notifications"
            >
              <Bell size={20} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-green-400 rounded-full" />
            </button>
          </div>
        </header>

        <main className="editor-panel-content flex-1 overflow-y-auto overflow-x-auto p-3 sm:p-4 md:p-6 bg-gray-50 min-h-0">
          <div className="max-w-[100vw] min-w-0">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

const getCurrentPageTitle = (path) => {
  if (path.includes('edit-blog')) return 'Edit Blog';
  if (path.includes('add-blog')) return 'Add Blog';
  if (path.includes('blog-list')) return 'Blog Management';
  if (path.includes('edit-advertisement')) return 'Edit Advertisement';
  if (path.includes('add-advertisement')) return 'Add Advertisement';
  if (path.includes('advertisement-list')) return 'Advertisement Management';

  const pathSegments = path.split('/');
  const lastSegment = pathSegments[pathSegments.length - 1];
  return lastSegment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const SidebarNavigation = ({ collapsed, currentPath, onNavigate }) => {
  useEffect(() => {
    if (typeof document === 'undefined' || document.head.querySelector('style[data-sidebar-scrollbar]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-sidebar-scrollbar', 'true');
    style.textContent =
      '.sidebar-nav-scrollbar::-webkit-scrollbar{width:6px}' +
      '.sidebar-nav-scrollbar::-webkit-scrollbar-track{background:#1f2937;border-radius:3px}' +
      '.sidebar-nav-scrollbar::-webkit-scrollbar-thumb{background:#4b5563;border-radius:3px}' +
      '.sidebar-nav-scrollbar{scrollbar-width:thin;scrollbar-color:#4b5563 #1f2937}';
    document.head.appendChild(style);
  }, []);

  const navItems = [
    { icon: FileText, label: 'Dashboard', href: '/editor/dashboard' },
    { icon: FileText, label: 'News List', href: '/editor/news-list' },
    { icon: PlusCircle, label: 'Add News', href: '/editor/add-news' },
    { icon: FileText, label: 'Blog List', href: '/editor/blog-list' },
    { icon: PlusCircle, label: 'Add Blog', href: '/editor/add-blog' },
    { icon: FileText, label: 'Advertisement List', href: '/editor/advertisement-list' },
    { icon: PlusCircle, label: 'Add Advertisement', href: '/editor/add-advertisement' },
    { icon: MessageSquare, label: 'Comments', href: '/editor/comments' },
    { icon: User, label: 'Profile', href: '/editor/profile' },
  ];

  return (
    <nav className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 sidebar-nav-scrollbar">
      {navItems.map((item) => (
        <NavItem
          key={item.href}
          icon={<item.icon size={20} />}
          label={item.label}
          isActive={currentPath.includes(item.href)}
          collapsed={collapsed}
          href={item.href}
          onNavigate={onNavigate}
        />
      ))}
    </nav>
  );
};

const NavItem = ({ icon, label, isActive, collapsed, href, onNavigate }) => (
  <Link
    to={href}
    onClick={onNavigate}
    className={`flex items-center py-2.5 sm:py-3 px-3 sm:px-4 text-sm sm:text-base touch-manipulation ${
      isActive ? 'bg-green-700 text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'
    }`}
  >
    <span className="shrink-0">{icon}</span>
    {!collapsed && <span className="ml-3 truncate">{label}</span>}
  </Link>
);

export default EditorLayout;
